var DashboardRepository = require("app/Domain/Ott/Api/V2/DataAccess/Dashboard/Repository"),
    DataTransformer     = require("./DataTransformer"),
    HeaderView          = require("app/AppHeader/HeaderView"),
    MediaRepository     = require("app/Domain/Ott/Api/V2/DataAccess/Media/Repository"),
    ViewController      = require("app/core/ViewController"),
    config              = require("app/config"),
    deepEqual           = require("fast-deep-equal"),
    icons               = require("app/theme/icons"),
    kind                = require("enyo/kind"),
    localeParser        = require("bcp-47").parse,
    logger              = require("enyo/logger"),
    quickMacroize       = require("enyo/macroize").quickMacroize,
    utils               = require("enyo/utils");

module.exports = kind(/** @lends  app.AppHeader.HeaderController.prototype */ {

    /**
     * @private
     */
    name: "app.AppHeader.HeaderController",

    /**
     * @private
     */
    kind: ViewController,

    /**
     *
     * The control that will host the view.
     *
     * @type     {?enyo.Control}
     * @default  null
     * @private
     */
    viewContainer: null,

    /**
     * @type     {boolean}
     * @default  'false'
     * @readonly
     * @public
     */
    isLoadingCategories: false,

    /**
     * @type     {boolean}
     * @default  'false'
     * @readonly
     * @public
     */
    isLoadingBrands: false,

    /**
     * @type     {boolean}
     * @default  'false'
     * @readonly
     * @public
     */
    isLoadingLanguages: false,

    /**
     * Whether or not to show a back button.
     *
     * @private
     */
    showBackButton: false,

    /**
     * State and data for this page.
     *
     * @type {?Object}
     * @readonly
     * @public
     */
    state: null,

    /**
     * @private
     */
    bindings: [{
        from: ".state",
        to:   ".view.state"
    }, {
        from: ".showBackButton",
        to:   ".view.showBackButton"
    }],

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.set("viewContainer", this.app.view.$.headerRegion);
            this.updateView(HeaderView);
        };
    }),

    /**
     * @returns  {Promise}  Whether the header was successfully refreshed.
     * @private
     */
    refresh: function() {
        var language = this.app.$.i18nService.get("language"),
            categoriesPromise,
            brandsPromise,
            languagesPromise;

        // Load categories
        this.set("isLoadingCategories", true);
        categoriesPromise = DashboardRepository.getRootTitles(false, language);
        categoriesPromise.then(this.bindSafely(function(response) {
            this.set("isLoadingCategories", false);

            return Promise.resolve(response);
        }));

        // Load brands
        this.set("isLoadingBrands", true);
        brandsPromise = MediaRepository.getTitlesByType(false, language, "brand");
        brandsPromise.then(this.bindSafely(function(response) {
            this.set("isLoadingBrands", false);

            return Promise.resolve(response);
        }));

        // Load languages
        this.set("isLoadingLanguages", true);
        languagesPromise = new Promise(this.bindSafely(function(resolve) {
            var supportedLocales = config.supportedLocales,
                languages        = [];

            supportedLocales.forEach(function(locale) {
                var language = localeParser(locale.replace("_", "-")).language;
                languages.push({
                    id:       locale,
                    locale:   locale,
                    language: language,
                    icon:     icons[quickMacroize("app.AppHeader.LanguageSwitcher.LanguageIcon.{$language}", {
                        language: language
                    })]
                });
            });
            this.set("isLoadingLanguages", false);

            resolve({
                status: "ok",
                data:   languages
            });
        }));

        this.updateState();

        return Promise.all([categoriesPromise, brandsPromise, languagesPromise])
            .then(this.bindSafely(function(responses) {
                var categoriesResponse = responses[0],
                    brandsResponse     = responses[1],
                    languagesResponse  = responses[2],
                    categoriesData,
                    brandsData,
                    languagesData;

                // Prepare data
                if (categoriesResponse.status === "ok") {
                    categoriesData = DataTransformer.transformCategories(language, categoriesResponse.data);
                }

                if (brandsResponse.status === "ok") {
                    brandsData = DataTransformer.transformBrands(language, brandsResponse.data);
                }

                if (languagesResponse.status === "ok") {
                    languagesData = languagesResponse.data;
                }

                this.updateState(categoriesData, brandsData, languagesData);
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }
                this.updateState();
            }));
    },

    /**
     * @param  {Array}  categories  Freshly loaded categories
     * @param  {Array}  brands      Freshly loaded brands
     * @param  {Array}  languages   Freshly loaded languages
     * @private
     */
    updateState: function(categories, brands, languages) {
        var state = utils.clone(this.state) || {};

        state.categories = categories || [];
        state.brands     = brands || [];
        state.languages  = languages || [];

        state.isLoadingCategories = this.isLoadingCategories;
        state.isLoadingBrands     = this.isLoadingBrands;
        state.isLoadingLanguages  = this.isLoadingLanguages;
        state.showBackButton      = this.showBackButton;

        // Update state only if it actually changed
        if (!deepEqual(this.state, state)) {
            this.set("state", state);
        }
    }

});
